import { useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactQuill, { Quill } from 'react-quill-new'
import QuillImageDropAndPaste from 'quill-image-drop-and-paste'

import api from '../../api/api'
import { getAssetLink } from '../../helpers/get-asset-link'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import 'react-quill-new/dist/quill.snow.css'

const Block = Quill.import('blots/block')

Block.tagName = 'DIV'
Quill.register(Block, true)
Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste)

const EditorText = ({
  onChange = () => {},
  value = '',
  disabled,
  placeholder = 'Your message...',
  imageType,
}) => {
  const { displaySnackbar } = useSnackbar()

  const editorRef = useRef()

  const uploadImage = async (file) => {
    try {
      const { data } = await api.uploadImage(imageType, file)

      const [fileName = ''] = data
      switch (imageType) {
        case 'email_attachments':
          return getAssetLink({ path: 'email-attachments', file: fileName })
        default:
          throw new Error('Invalid image type')
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      displaySnackbar(error.message || 'Error uploading image', true)
    }
  }

  const validateImage = (file) => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png']
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Unsupported file type. Please upload an image in JPEG or PNG format.')
    }

    // Check file size (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024
    if (file.size > maxSize) {
      throw new Error('File size exceeds the 2MB limit. Please upload a smaller image.')
    }
  }

  const uploadAndInsertImage = async (file) => {
    const quillEditor = editorRef.current.getEditor()
    const range = quillEditor.getSelection(true)
    try {
      validateImage(file)

      quillEditor.insertText(range.index, '[Uploading image...]', 'italic')

      const imageUrl = await uploadImage(file)

      quillEditor.deleteText(range.index, '[Uploading image...]'.length)

      quillEditor.insertEmbed(range.index, 'image', imageUrl, 'user')

      quillEditor.setSelection(range.index + 1, 0)
    } catch (error) {
      console.error('Failed to upload image:', error)
      displaySnackbar(error.message || 'Failed to upload image', true)
      quillEditor.deleteText(range.index, '[Uploading image...]'.length)
    }
  }

  const inputImageHandler = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = async () => {
      const file = input.files[0]
      const reader = new FileReader()
      uploadAndInsertImage(file)
      reader.readAsDataURL(file)
    }
  }, [])

  const dragAndDropImageHandler = useCallback(async (imageDataUrl, type, imageData) => {
    const file = imageData.toFile()
    uploadAndInsertImage(file)
  }, [])

  const modules = useMemo(() => {
    const settings = {
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link'],
          // ['clean']
        ],
      },
    }

    if (imageType) {
      settings.toolbar.container.push(['image'])
      settings.toolbar = {
        ...settings.toolbar,
        handlers: {
          image: inputImageHandler,
        },
      }
      settings.imageDropAndPaste = {
        handler: dragAndDropImageHandler,
      }
    }

    return settings
  }, [inputImageHandler, dragAndDropImageHandler, imageType])

  const formats = useMemo(() => {
    const formats = [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'blockquote',
      'list',
      'indent',
      'link',
    ]
    if (imageType) formats.push('image')
    return formats
  }, [imageType])

  const onChangeHere = (content, delta, source, editor) => {
    const text = editor.getText()
    onChange(content, text)
  }

  return (
    <ReactQuill
      ref={editorRef}
      theme="snow"
      placeholder={placeholder}
      onChange={onChangeHere}
      modules={modules}
      formats={formats}
      value={value}
      readOnly={disabled}
    />
  )
}

EditorText.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  imageType: PropTypes.string,
}

export default EditorText
